import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';

import alert from '@drivesally/sally-ui/lib/libs/alert';
import fkOrId from '@drivesally/sally-fw/lib/lib/fkOrId';
import parseError from '@drivesally/sally-fw/lib/error/parseError';
import subscriptionHOC from '@drivesally/sally-fw/lib/event/hoc/subscription.hoc.event';

import createShortcutApi from '../../api/createShortcut.api.crm';
import deleteShortcutApi from '../../api/deleteShortcut.api.crm';
import listShortcutsApi from '../../api/listShortcuts.api.crm';
import listTagsApi from '../../api/listTags.api.crm';
import listTeamsApi from '../../api/listTeams.api.crm';

import CrmShortcutsModal from '../../components/CrmShortcutsModal/CrmShortcutsModal';

import showShortcutsModalEvent from '../../events/showShortcutsModal.event.crm';

const sortShortcuts = (shortcuts) =>
  [...shortcuts].sort((a, b) => (b.id || 0) - (a.id || 0));

class CrmShortcutsContainer extends Component {
  static propTypes = {
    subscribe: PropTypes.func,
    userData: PropTypes.object,
    users: PropTypes.array,
  };

  state = {
    assignToMyself: false,
    creating: false,
    deletingId: null,
    filterTeam: 'all',
    filterUser: 'all',
    loading: false,
    nameInputKey: 0,
    newMessage: '',
    newName: '',
    newTeamId: '',
    search: '',
    selectedTagIds: [],
    shortcuts: [],
    tags: [],
    tagsById: {},
    teams: [],
    teamsById: {},
    visible: false,
  };

  componentDidMount() {
    this.mounted = true;
    this.props.subscribe(showShortcutsModalEvent.subscribe(this.show));
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  show = () => {
    this.setState(
      {
        visible: true,
        assignToMyself: false,
        filterTeam: 'all',
        filterUser: 'all',
        newMessage: '',
        newName: '',
        newTeamId: '',
        search: '',
        selectedTagIds: [],
      },
      this.load
    );
  };

  hide = () => {
    const {creating, deletingId, loading} = this.state;
    if (creating || deletingId || loading) return;
    this.setState({visible: false});
  };

  load = async ({search = this.state.search} = {}) => {
    this.setState({loading: true, search});
    try {
      const query = {
        limit: 1000,
        ordering: '-id',
      };
      const trimmedSearch = search.trim();
      if (trimmedSearch) query.search = trimmedSearch;

      const [shortcutsResponse, teamsResponse, tagsResponse] =
        await Promise.all([
          listShortcutsApi(query),
          listTeamsApi({limit: 100, ordering: 'name'}),
          listTagsApi({limit: 1000, ordering: 'tag'}),
        ]);
      if (!this.mounted) return;
      const teams = teamsResponse.results || [];
      const tags = tagsResponse.results || [];
      this.setState({
        loading: false,
        shortcuts: sortShortcuts(shortcutsResponse.results || []),
        tags,
        tagsById: tags.reduce(
          (lookup, tag) => ({...lookup, [tag.id]: tag}),
          {}
        ),
        teams,
        teamsById: teams.reduce(
          (lookup, team) => ({...lookup, [team.id]: team}),
          {}
        ),
      });
    } catch (error) {
      if (!this.mounted) return;
      alert.info(`Failed to fetch CRM shortcuts: ${error}`);
      this.setState({
        loading: false,
        shortcuts: [],
        tags: [],
        tagsById: {},
        teams: [],
        teamsById: {},
      });
    }
  };

  onSearch = (search) => {
    this.load({search});
  };

  onNewNameChange = (newName) => {
    this.setState({newName});
  };

  onNewMessageChange = (newMessage) => {
    this.setState({newMessage});
  };

  onNewTeamChange = (newTeamId) => {
    this.setState({newTeamId, assignToMyself: false});
  };

  onAssignToMyselfChange = (assignToMyself) => {
    this.setState({
      assignToMyself,
      newTeamId: assignToMyself ? '' : this.state.newTeamId,
    });
  };

  onTagToggle = (tagId) => () => {
    const {selectedTagIds} = this.state;
    this.setState({
      selectedTagIds: selectedTagIds.includes(tagId)
        ? selectedTagIds.filter((id) => id !== tagId)
        : [...selectedTagIds, tagId],
    });
  };

  onFilterTeamChange = (filterTeam) => {
    this.setState({filterTeam});
  };

  onFilterUserChange = (filterUser) => {
    this.setState({filterUser});
  };

  filteredShortcuts = () => {
    const {filterTeam, filterUser, shortcuts} = this.state;
    const userId = this.props.userData?.id;

    return shortcuts.filter((shortcut) => {
      const shortcutTeamId = fkOrId(shortcut.team);
      const shortcutUserId = fkOrId(shortcut.user);

      if (filterTeam === 'none' && shortcutTeamId) return false;
      if (
        filterTeam !== 'all' &&
        filterTeam !== 'none' &&
        `${shortcutTeamId || ''}` !== filterTeam
      ) {
        return false;
      }

      if (filterUser === 'mine' && shortcutUserId !== userId) return false;
      if (filterUser === 'unassigned' && shortcutUserId) return false;

      return true;
    });
  };

  create = async () => {
    const {
      assignToMyself,
      creating,
      newMessage,
      newName,
      newTeamId,
      selectedTagIds,
    } = this.state;
    const name = newName.trim();
    const message = newMessage.trim();
    const userId = this.props.userData?.id;

    if (creating || !name.length || !message.length) return;
    if (assignToMyself && !userId) {
      alert.error('Unable to assign shortcut without a signed-in user');
      return;
    }

    this.setState({creating: true});
    try {
      const payload = {
        name,
        message,
        team: assignToMyself ? null : newTeamId ? Number(newTeamId) : null,
        user: assignToMyself ? userId : null,
        tags: selectedTagIds,
      };
      const response = await createShortcutApi(payload);
      if (!this.mounted) return;
      this.setState({
        creating: false,
        assignToMyself: false,
        nameInputKey: this.state.nameInputKey + 1,
        newMessage: '',
        newName: '',
        newTeamId: '',
        selectedTagIds: [],
        shortcuts: sortShortcuts([...this.state.shortcuts, response]),
      });
    } catch (error) {
      const {message: errorMessage} = parseError(error);
      alert.error(errorMessage);
      if (!this.mounted) return;
      this.setState({creating: false});
    }
  };

  remove = (shortcut) => async () => {
    const {creating, deletingId} = this.state;
    if (creating || deletingId) return;

    this.setState({deletingId: shortcut.id});
    try {
      await deleteShortcutApi(shortcut.id);
      if (!this.mounted) return;
      this.setState({
        deletingId: null,
        shortcuts: this.state.shortcuts.filter(({id}) => id !== shortcut.id),
      });
    } catch (error) {
      const {message} = parseError(error);
      alert.error(message);
      if (!this.mounted) return;
      this.setState({deletingId: null});
    }
  };

  render() {
    const {
      assignToMyself,
      creating,
      deletingId,
      filterTeam,
      filterUser,
      loading,
      nameInputKey,
      newMessage,
      newName,
      newTeamId,
      search,
      selectedTagIds,
      tags,
      tagsById,
      teams,
      teamsById,
      visible,
    } = this.state;

    return (
      <CrmShortcutsModal
        assignToMyself={assignToMyself}
        creating={creating}
        deletingId={deletingId}
        filterTeam={filterTeam}
        filterUser={filterUser}
        loading={loading}
        nameInputKey={nameInputKey}
        newMessage={newMessage}
        newName={newName}
        newTeamId={newTeamId}
        onAssignToMyselfChange={this.onAssignToMyselfChange}
        onClose={this.hide}
        onCreate={this.create}
        onDelete={this.remove}
        onFilterTeamChange={this.onFilterTeamChange}
        onFilterUserChange={this.onFilterUserChange}
        onNewMessageChange={this.onNewMessageChange}
        onNewNameChange={this.onNewNameChange}
        onNewTeamChange={this.onNewTeamChange}
        onSearch={this.onSearch}
        onTagToggle={this.onTagToggle}
        search={search}
        selectedTagIds={selectedTagIds}
        shortcuts={this.filteredShortcuts()}
        tags={tags}
        tagsById={tagsById}
        teams={teams}
        teamsById={teamsById}
        users={this.props.users}
        visible={visible}
      />
    );
  }
}

export default connect((state) => ({
  userData: state.authorization.userData,
  users: state.user.users,
}))(subscriptionHOC(CrmShortcutsContainer));
